/**
 * Referências de colunas multi-fonte: { id, fonte } e chave composta fonte:id.
 */

import {
  FONTE_CHAMADOS_TECNICOS,
  FONTE_PARQUE_LOCADO,
  FONTE_PRODUCAO_VOLUMETRIA,
  FONTES_DADOS_OPTIONS,
} from '../constants/relatoriosFontes';
import {
  PRODUCAO_MES_COLUMN_ID,
  PRODUCAO_VOLUMETRIA_COLUMN_IDS,
} from '../constants/relatoriosColumnsProducaoVolumetria';
import {
  isProducaoCounterColumnId,
  isProducaoVolumetriaMetricColumnId,
  normalizeProducaoDisplayMode,
  PROD_DISPLAY_MES,
} from './relatoriosProducaoPivot';
import { Activity } from 'lucide-react';
import { getRelatoriosColumnSet } from '../constants/relatoriosColumnsRegistry';
import { normalizePivotSelecionados, PIVOT_COLUMN_CONFIG } from './relatoriosPivotColumns';
import { COLUMNS_CONFIG_PECAS, PECAS_COLUMN_IDS, PECAS_MAIN_ROW_COLUMN_IDS } from '../constants/relatoriosColumnsPecas';
import { getPecasMainRowValue } from './relatoriosPecas';

/** Colunas chamados usadas só como complemento de parque (não listadas na fonte Chamados). */
export const CHAMADOS_PARQUE_LINKED_META = {
  uptime: { id: 'uptime', label: 'Uptime', icon: Activity },
};

const FONTE_SHORT = {
  [FONTE_PARQUE_LOCADO]: 'Parque',
  [FONTE_CHAMADOS_TECNICOS]: 'Chamados',
  [FONTE_PRODUCAO_VOLUMETRIA]: 'Produção',
};

/** Fontes complementares permitidas por fonte principal do relatório. */
const COMPLEMENTARY_BY_PRIMARY = {
  [FONTE_PARQUE_LOCADO]: [FONTE_CHAMADOS_TECNICOS, FONTE_PRODUCAO_VOLUMETRIA],
  [FONTE_CHAMADOS_TECNICOS]: [],
};

/** Colunas complementares liberadas por fonte (parque → chamados). */
const COMPLEMENTARY_COLUMN_WHITELIST = {
  [FONTE_PARQUE_LOCADO]: {
    [FONTE_CHAMADOS_TECNICOS]: [
      'os',
      'uptime',
      'tempoProduzido',
      'contadorA4Pb',
      'contadorA4Cor',
      'contadorA3Pb',
      'contadorA3Cor',
      'contadorDig',
      'fimAtendimento',
      'defeito',
      'condicaoFinal',
      'tipoOs',
      ...PECAS_COLUMN_IDS,
    ],
    [FONTE_PRODUCAO_VOLUMETRIA]: PRODUCAO_VOLUMETRIA_COLUMN_IDS,
  },
};

export const CHAMADOS_COUNTER_COLUMN_IDS = [
  'contadorA4Pb',
  'contadorA4Cor',
  'contadorA3Pb',
  'contadorA3Cor',
  'contadorDig',
];

export const CHAMADOS_LATEST_VALUE_COLUMN_IDS = [...CHAMADOS_COUNTER_COLUMN_IDS, 'fimAtendimento'];

const LINKED_INFO_TITLES = {
  uptime: 'Percentual de disponibilidade no per\u00edodo de OS (dias sem ordem aberta)',
  contadorA4Pb: '\u00daltimo contador registrado',
  contadorA4Cor: '\u00daltimo contador registrado',
  contadorA3Pb: '\u00daltimo contador registrado',
  contadorA3Cor: '\u00daltimo contador registrado',
  contadorDig: '\u00daltimo contador registrado',
  fimAtendimento: 'Mostra o \u00faltimo atendimento realizado',
};

export function isComplementaryColumnAllowed(primaryFonte, columnFonte, columnId) {
  if (columnId === 'uptime' && columnFonte === FONTE_CHAMADOS_TECNICOS) {
    return primaryFonte === FONTE_PARQUE_LOCADO;
  }
  if (columnFonte === primaryFonte) return true;
  const allowed = COMPLEMENTARY_COLUMN_WHITELIST[primaryFonte]?.[columnFonte];
  if (!Array.isArray(allowed)) return false;
  return allowed.includes(columnId);
}

export function filterColumnRefsByPolicy(refs, primaryFonte) {
  return (refs || []).filter((r) => isComplementaryColumnAllowed(primaryFonte, r.fonte, r.id));
}

/** Coluna complementar de Chamados técnicos ou Produção (volumetria) no relatório. */
export function reportUsesComplementaryChamadosOrProducao(columnRefs) {
  return (columnRefs || []).some(
    (r) =>
      r?.fonte === FONTE_CHAMADOS_TECNICOS || r?.fonte === FONTE_PRODUCAO_VOLUMETRIA
  );
}

/** Relatório inclui campos das duas fontes usadas na apresentação modelo 1. */
export function reportHasChamadosAndProducaoFields(columnRefs) {
  const refs = columnRefs || [];
  const hasChamados = refs.some((r) => r?.fonte === FONTE_CHAMADOS_TECNICOS);
  const hasProducao = refs.some((r) => r?.fonte === FONTE_PRODUCAO_VOLUMETRIA);
  return hasChamados && hasProducao;
}

export function columnRefKey(fonte, id) {
  return `${fonte}:${id}`;
}

export const CHAMADOS_OS_COLUMN_KEY = columnRefKey(FONTE_CHAMADOS_TECNICOS, 'os');
export const CHAMADOS_UPTIME_COLUMN_KEY = columnRefKey(FONTE_CHAMADOS_TECNICOS, 'uptime');
export const CHAMADOS_TEMPO_PRODUZIDO_COLUMN_KEY = columnRefKey(
  FONTE_CHAMADOS_TECNICOS,
  'tempoProduzido'
);

/** Coluna OS (chamados) presente no relatório parque — exigida para Uptime. */
export function hasParqueOsRef(columnRefs) {
  return (columnRefs || []).some(
    (r) => r.fonte === FONTE_CHAMADOS_TECNICOS && r.id === 'os'
  );
}

export function stripUptimeWithoutOs(columnRefs) {
  if (hasParqueOsRef(columnRefs)) return columnRefs;
  return (columnRefs || []).filter(
    (r) => !(r.fonte === FONTE_CHAMADOS_TECNICOS && r.id === 'uptime')
  );
}

export function parseColumnRefKey(key) {
  if (!key || typeof key !== 'string') return null;
  const i = key.indexOf(':');
  if (i <= 0) return null;
  return { fonte: key.slice(0, i), id: key.slice(i + 1) };
}

/** ID de agrupamento salvo no relatório → id da coluna TanStack (fonte:id). */
export function groupIdToColumnKey(groupId, primaryFonte) {
  if (!groupId) return groupId;
  const s = String(groupId).trim();
  if (s.includes(':')) return s;
  return columnRefKey(primaryFonte, s);
}

/** Resolve agrupamento para ids de coluna presentes na tabela. */
export function resolveGroupingColumnIds(groupBy, primaryFonte, visibleColumnIds = []) {
  const arr = Array.isArray(groupBy) ? groupBy : groupBy ? [groupBy] : [];
  const visible = Array.isArray(visibleColumnIds) ? visibleColumnIds : [];
  return arr
    .map((id) => {
      const key = groupIdToColumnKey(id, primaryFonte);
      if (visible.length === 0) return key;
      if (visible.includes(key)) return key;
      const match = visible.find((k) => {
        const p = parseColumnRefKey(k);
        return p?.id === id || k === id;
      });
      return match || key;
    })
    .filter((key, index, all) => key && all.indexOf(key) === index);
}

export const OS_AGG_CONTAGEM = 'contagem';
export const OS_AGG_MEDIA = 'media';

export function normalizeOsAgg(value) {
  return value === OS_AGG_MEDIA ? OS_AGG_MEDIA : OS_AGG_CONTAGEM;
}

/** Rótulo da coluna OS no relatório (sufixo quando agregação = média). */
export function getOsColumnHeaderLabel(baseLabel, osAgg) {
  const base = String(baseLabel || 'OS').trim() || 'OS';
  if (normalizeOsAgg(osAgg) === OS_AGG_MEDIA) {
    return `${base} (m\u00e9dia)`;
  }
  return base;
}

/** Labels de exibição da coluna OS conforme agregação (parque locado). */
export function buildOsColumnLabels(columnRefs, primaryFonte) {
  if (primaryFonte !== FONTE_PARQUE_LOCADO) return {};
  const osRef = (columnRefs || []).find(
    (r) => r.id === 'os' && r.fonte === FONTE_CHAMADOS_TECNICOS
  );
  if (!osRef) return {};
  const meta = getColumnMeta(FONTE_CHAMADOS_TECNICOS, 'os');
  const base = meta?.label || 'OS';
  const label = getOsColumnHeaderLabel(base, osRef.osAgg);
  if (label === base) return {};
  return { [CHAMADOS_OS_COLUMN_KEY]: label };
}

export function normalizeOsPeriodo(item) {
  if (!item || typeof item !== 'object') return null;
  const startDate = item.startDate ? String(item.startDate).trim() : '';
  const endDate = item.endDate ? String(item.endDate).trim() : '';
  if (!startDate || !endDate || startDate > endDate) return null;
  return { startDate, endDate };
}

export function normalizeColumnRef(item, defaultFonte = FONTE_CHAMADOS_TECNICOS) {
  if (typeof item === 'string' && item.trim()) {
    const id = item.trim();
    const ref = { id, fonte: defaultFonte };
    if (id === 'os' && ref.fonte === FONTE_CHAMADOS_TECNICOS) ref.osAgg = OS_AGG_CONTAGEM;
    return ref;
  }
  if (item && typeof item === 'object' && item.id) {
    const ref = {
      id: String(item.id).trim(),
      fonte: String(item.fonte || defaultFonte).trim() || defaultFonte,
    };
    if (ref.id === 'os' && ref.fonte === FONTE_CHAMADOS_TECNICOS) {
      ref.osAgg = normalizeOsAgg(item.osAgg);
      const periodo = normalizeOsPeriodo(item.osPeriodo);
      if (periodo) ref.osPeriodo = periodo;
    }
    if (ref.id === PRODUCAO_MES_COLUMN_ID && ref.fonte === FONTE_PRODUCAO_VOLUMETRIA) {
      const periodo = normalizeOsPeriodo(item.mesPeriodo);
      if (periodo) ref.mesPeriodo = periodo;
      ref.prodDisplayMode = normalizeProducaoDisplayMode(item.prodDisplayMode);
    }
    if (ref.fonte === FONTE_PRODUCAO_VOLUMETRIA && isProducaoCounterColumnId(ref.id)) {
      ref.prodSparkChart = item.prodSparkChart === true;
    }
    for (const columnId of Object.keys(PIVOT_COLUMN_CONFIG)) {
      const cfg = PIVOT_COLUMN_CONFIG[columnId];
      if (ref.id === columnId && ref.fonte === FONTE_CHAMADOS_TECNICOS) {
        if (Array.isArray(item[cfg.selecionadosKey])) {
          ref[cfg.selecionadosKey] = normalizePivotSelecionados(item[cfg.selecionadosKey]);
        }
      }
    }
    return ref;
  }
  return null;
}

export function normalizeColumnRefs(colunas, defaultFonte = FONTE_CHAMADOS_TECNICOS) {
  if (!Array.isArray(colunas) || colunas.length === 0) return [];
  const out = [];
  const seen = new Set();
  for (const item of colunas) {
    const ref = normalizeColumnRef(item, defaultFonte);
    if (!ref?.id) continue;
    const key = columnRefKey(ref.fonte, ref.id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(ref);
  }
  return out;
}

export function refsToKeys(refs) {
  return (refs || []).map((r) => columnRefKey(r.fonte, r.id));
}

export function keysToRefs(keys, defaultFonte = FONTE_CHAMADOS_TECNICOS, prevRefs = []) {
  const prevMap = new Map((prevRefs || []).map((r) => [columnRefKey(r.fonte, r.id), r]));
  const out = [];
  const seen = new Set();
  for (const key of keys || []) {
    const parsed = parseColumnRefKey(key);
    const k = parsed ? columnRefKey(parsed.fonte, parsed.id) : key;
    const ref =
      prevMap.get(k) ||
      (parsed ? normalizeColumnRef(parsed, defaultFonte) : normalizeColumnRef(key, defaultFonte));
    if (!ref) continue;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(ref);
  }
  return out;
}

export function isParqueOsColumn(ref, primaryFonte) {
  return (
    primaryFonte === FONTE_PARQUE_LOCADO &&
    ref?.id === 'os' &&
    ref?.fonte === FONTE_CHAMADOS_TECNICOS
  );
}

/** Coluna OS em parque: agregação e período próprios. */
export function isParqueOsAggColumn(ref, primaryFonte) {
  return isParqueOsColumn(ref, primaryFonte);
}

export function isParqueUptimeColumn(ref, primaryFonte) {
  return (
    primaryFonte === FONTE_PARQUE_LOCADO &&
    ref?.id === 'uptime' &&
    ref?.fonte === FONTE_CHAMADOS_TECNICOS
  );
}

/** Exibição da OS enriquecida em parque: sem dado → zero. */
export function formatParqueOsCellValue(value) {
  if (value == null || value === '' || value === '-') return '0';
  return String(value).trim();
}

export const OS_EXPORT_DECIMAL_PLACES = 5;

export function getOsAggFromColumnRefs(columnRefs) {
  const osRef = (columnRefs || []).find(
    (r) => r.id === 'os' && r.fonte === FONTE_CHAMADOS_TECNICOS
  );
  return normalizeOsAgg(osRef?.osAgg);
}

export function isParqueOsMediaAgg(columnRefs) {
  return getOsAggFromColumnRefs(columnRefs) === OS_AGG_MEDIA;
}

export function parseParqueOsNumeric(value) {
  if (value == null || value === '' || value === '-') return null;
  let s = String(value).trim();
  if (!s) return null;
  if (s.includes(',')) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (/^\d{1,3}(\.\d{3})+$/.test(s)) {
    s = s.replace(/\./g, '');
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Valor da coluna OS na exportação Excel (média = número com 5 decimais; contagem = inteiro). */
export function formatParqueOsExportValue(value, osAgg) {
  if (normalizeOsAgg(osAgg) === OS_AGG_MEDIA) {
    const n = parseParqueOsNumeric(value);
    return n == null ? 0 : Number(n.toFixed(OS_EXPORT_DECIMAL_PLACES));
  }
  return formatParqueOsCellValue(value);
}

/** Ciclo mensal (vida útil): inteiro com separador de milhar pt-BR. */
export function formatCicloMensalCellValue(value) {
  if (value == null || value === '' || value === '-') return '-';
  const n = Number(String(value).replace(/\D/g, ''));
  if (Number.isNaN(n)) return String(value).trim() || '-';
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

export function parseCicloMensalSortValue(value) {
  if (value == null || value === '' || value === '-') return -1;
  const n = Number(String(value).replace(/\D/g, ''));
  return Number.isNaN(n) ? -1 : n;
}

export function isParqueCicloMensalColumn(ref, primaryFonte) {
  return primaryFonte === FONTE_PARQUE_LOCADO && ref?.fonte === FONTE_PARQUE_LOCADO && ref?.id === 'cicloMensal';
}

export function isParquePecasColumn(ref, primaryFonte) {
  return (
    primaryFonte === FONTE_PARQUE_LOCADO &&
    ref?.fonte === FONTE_CHAMADOS_TECNICOS &&
    PECAS_COLUMN_IDS.includes(ref?.id)
  );
}

export function isParqueChamadosEnrichColumn(ref, primaryFonte) {
  return (
    primaryFonte === FONTE_PARQUE_LOCADO &&
    ref?.fonte === FONTE_CHAMADOS_TECNICOS &&
    (ref?.id === 'os' ||
      ref?.id === 'uptime' ||
      ref?.id === 'tempoProduzido' ||
      Object.keys(PIVOT_COLUMN_CONFIG).includes(ref?.id) ||
      CHAMADOS_LATEST_VALUE_COLUMN_IDS.includes(ref.id) ||
      PECAS_COLUMN_IDS.includes(ref.id))
  );
}

export function isParqueMesColumn(ref, primaryFonte) {
  return (
    primaryFonte === FONTE_PARQUE_LOCADO &&
    ref?.fonte === FONTE_PRODUCAO_VOLUMETRIA &&
    ref?.id === PRODUCAO_MES_COLUMN_ID
  );
}

export function isParqueProducaoCounterColumn(ref, primaryFonte) {
  return (
    primaryFonte === FONTE_PARQUE_LOCADO &&
    ref?.fonte === FONTE_PRODUCAO_VOLUMETRIA &&
    isProducaoVolumetriaMetricColumnId(ref?.id)
  );
}

export function isParqueProducaoEnrichColumn(ref, primaryFonte) {
  return isParqueMesColumn(ref, primaryFonte) || isParqueProducaoCounterColumn(ref, primaryFonte);
}

export function isParqueLinkedEnrichColumn(ref, primaryFonte) {
  return isParqueChamadosEnrichColumn(ref, primaryFonte) || isParqueProducaoEnrichColumn(ref, primaryFonte);
}

export function isParquePivotColumn(ref, primaryFonte, columnId) {
  return (
    primaryFonte === FONTE_PARQUE_LOCADO &&
    ref?.fonte === FONTE_CHAMADOS_TECNICOS &&
    ref?.id === columnId
  );
}

export function isParqueDefeitoColumn(ref, primaryFonte) {
  return isParquePivotColumn(ref, primaryFonte, 'defeito');
}

export function isParqueCondicaoColumn(ref, primaryFonte) {
  return isParquePivotColumn(ref, primaryFonte, 'condicaoFinal');
}

export function isParqueContadorColumn(ref, primaryFonte) {
  return (
    primaryFonte === FONTE_PARQUE_LOCADO &&
    ref?.fonte === FONTE_CHAMADOS_TECNICOS &&
    CHAMADOS_COUNTER_COLUMN_IDS.includes(ref?.id)
  );
}

export function isParqueLatestValueColumn(ref, primaryFonte) {
  return (
    primaryFonte === FONTE_PARQUE_LOCADO &&
    ref?.fonte === FONTE_CHAMADOS_TECNICOS &&
    CHAMADOS_LATEST_VALUE_COLUMN_IDS.includes(ref?.id)
  );
}

export function getLinkedColumnTitle(ref, primaryFonte) {
  if (isParqueUptimeColumn(ref, primaryFonte)) return LINKED_INFO_TITLES.uptime || null;
  if (!isParqueLatestValueColumn(ref, primaryFonte)) return null;
  return LINKED_INFO_TITLES[ref.id] || null;
}

export function getComplementaryFontes(primaryFonte) {
  return COMPLEMENTARY_BY_PRIMARY[primaryFonte] || [];
}

export function getFontesForSidebar(primaryFonte) {
  return [primaryFonte, ...getComplementaryFontes(primaryFonte)];
}

export function getColumnMeta(fonte, id) {
  const set = getRelatoriosColumnSet(fonte);
  const found = set.columns.find((c) => c.id === id);
  if (found) return found;
  if (fonte === FONTE_CHAMADOS_TECNICOS && CHAMADOS_PARQUE_LINKED_META[id]) {
    return CHAMADOS_PARQUE_LINKED_META[id];
  }
  if (fonte === FONTE_CHAMADOS_TECNICOS) {
    const peca = COLUMNS_CONFIG_PECAS.find((c) => c.id === id);
    if (peca) return peca;
  }
  return null;
}

export function getFonteShortLabel(fonte) {
  return FONTE_SHORT[fonte] || FONTES_DADOS_OPTIONS.find((f) => f.id === fonte)?.label?.split(' ')[0] || fonte;
}

/** Rótulo na UI; desambigua homônimos entre fontes. */
export function getColumnDisplayLabel(ref, primaryFonte) {
  const meta = getColumnMeta(ref.fonte, ref.id);
  const base = meta?.label || ref.id;
  const homonyms = new Set();
  for (const f of getFontesForSidebar(primaryFonte)) {
    const m = getColumnMeta(f, ref.id);
    if (m) homonyms.add(f);
  }
  if (homonyms.size > 1 && ref.fonte !== primaryFonte) {
    return `${base} (${getFonteShortLabel(ref.fonte)})`;
  }
  if (homonyms.size > 1 && ref.fonte === primaryFonte) {
    return `${base} (${getFonteShortLabel(ref.fonte)})`;
  }
  return base;
}

export function buildColumnSidebarItem(ref, primaryFonte) {
  const meta = getColumnMeta(ref.fonte, ref.id);
  if (!meta) return null;
  return {
    ...meta,
    id: ref.id,
    fonte: ref.fonte,
    key: columnRefKey(ref.fonte, ref.id),
    label: meta?.label || ref.id,
    fonteShort: getFonteShortLabel(ref.fonte),
  };
}

export function getDefaultColumnRefs(fonteDados) {
  const set = getRelatoriosColumnSet(fonteDados);
  return (set.defaultVisible || []).map((id) => ({ id, fonte: fonteDados }));
}

export function getReportColumnRefs(report) {
  const fonte = report?.fonte_dados || FONTE_CHAMADOS_TECNICOS;
  const colSet = getRelatoriosColumnSet(fonte);
  const raw = Array.isArray(report?.colunas) && report.colunas.length > 0 ? report.colunas : colSet.defaultVisible;
  let refs = filterColumnRefsByPolicy(normalizeColumnRefs(raw, fonte), fonte);
  if (fonte === FONTE_PARQUE_LOCADO) refs = stripUptimeWithoutOs(refs);
  if (refs.length > 0) return refs;
  return getDefaultColumnRefs(fonte);
}

/** Valor da célula na tabela/exportação. */
export function getRowCellValue(row, ref, primaryFonte) {
  if (!row || !ref) return '';
  if (isParquePecasColumn(ref, primaryFonte)) {
    if (PECAS_MAIN_ROW_COLUMN_IDS.includes(ref.id)) {
      return getPecasMainRowValue(row, ref.id);
    }
    return '';
  }
  const prefixed = columnRefKey(ref.fonte, ref.id);
  // Colunas da fonte principal (ex.: parque_locado) vêm no id simples; chave prefixada vazia não pode mascarar.
  if (ref.fonte === primaryFonte) {
    const plain = row[ref.id];
    if (plain != null && plain !== '') return plain;
    const pref = row[prefixed];
    if (pref != null && pref !== '') return pref;
    if (plain != null) return plain;
    return '';
  }
  if (row[prefixed] != null && row[prefixed] !== '') return row[prefixed];
  const v = row[prefixed] ?? row[ref.id];
  if (v == null || v === '') return '';
  return v;
}

export function serializeColumnRefsForCompare(refs) {
  return (refs || []).map((r) => {
    const base = `${r.fonte}:${r.id}`;
    if (r.id === 'os' && r.fonte === FONTE_CHAMADOS_TECNICOS) {
      const p = r.osPeriodo;
      const periodKey = p?.startDate && p?.endDate ? `:${p.startDate}-${p.endDate}` : '';
      return `${base}:${normalizeOsAgg(r.osAgg)}${periodKey}`;
    }
    if (r.id === PRODUCAO_MES_COLUMN_ID && r.fonte === FONTE_PRODUCAO_VOLUMETRIA) {
      const p = r.mesPeriodo;
      const periodKey = p?.startDate && p?.endDate ? `:${p.startDate}-${p.endDate}` : '';
      const modeKey = r.prodDisplayMode && r.prodDisplayMode !== PROD_DISPLAY_MES ? `:${r.prodDisplayMode}` : '';
      return `${base}${periodKey}${modeKey}`;
    }
    if (r.fonte === FONTE_PRODUCAO_VOLUMETRIA && isProducaoCounterColumnId(r.id) && r.prodSparkChart) {
      return `${base}:grafico`;
    }
    const pivotCfg = PIVOT_COLUMN_CONFIG[r.id];
    if (pivotCfg && r.fonte === FONTE_CHAMADOS_TECNICOS) {
      const raw = r[pivotCfg.selecionadosKey];
      if (Array.isArray(raw) && raw.length === 0) return `${base}:__none__`;
      const sel = normalizePivotSelecionados(raw);
      return sel && sel.length > 0 ? `${base}:${sel.join('|')}` : base;
    }
    return base;
  });
}

export function columnRefsEqual(a, b) {
  const ka = serializeColumnRefsForCompare(normalizeColumnRefs(a));
  const kb = serializeColumnRefsForCompare(normalizeColumnRefs(b));
  return ka.length === kb.length && ka.every((k, i) => k === kb[i]);
}
